import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {Box, Chip, CircularProgress, Divider, IconButton, Stack, Tab, Typography, useMediaQuery} from "@mui/material";
import {TabContext} from "@mui/lab";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {
    AgendaService
} from "crqlar-react-common/dist/services/agenda/agenda.service";
import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import AgendaDetailsBasicTab from "./AgendaDetailsBasicTab";
import AgendaDetailsLocalizationTab from "./AgendaDetailsLocalizationTab";
import AgendaDetailsStylingTab from "./AgendaDetailsStylingTab";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import Button from "@mui/material/Button";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
    useAllSectors,
} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {
    ServiceService
} from "crqlar-react-common/dist/services/service/service.service";
import {
    ReservationActions,
    useReservationsState
} from "../../../store/reservations.reducer";
import {useDispatch} from "react-redux";
import {
    useCurrentAppId
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useQueryClient} from "react-query";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {useTheme} from "@mui/material/styles";
import {isRequiredTextMissing} from "src/utils/requiredField.util";


const AgendaDetails = () => {

    //region State

    useTrackScreenVisit('agenda details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const sectors = useAllSectors()
    const weeklyPlannerSector = sectors.find(s => s.sectorType === SectorType.WEEKLY_PLANNER)
    const dispatch = useDispatch()
    const appId = useCurrentAppId()

    const queryClient = useQueryClient()

    const {
        activeTab,
    } = useReservationsState(appId)

    const agendaData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return AgendaService.getAgenda(id)
            } else {
                return {} as AgendaV3
            }
        },
        create: AgendaService.createAgenda,
        update: AgendaService.updateAgenda,
        delete: (id) => AgendaService.deleteAgenda({id: [id]}),
        onDeleted: () => navigate('/system/agenda', {replace: true}),
        onSaved: async (agenda, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/agenda/${agenda.id}`, {replace: true})
            } else {
                // update the selected services automatically
                const key = `selectedServices-${weeklyPlannerSector?.id}-${activeTab}`;
                const serviceIds = agenda.serviceIds

                const response = await ServiceService.getAllServices({
                    id: serviceIds,
                    limit: {
                        offset: 0,
                        limit: 1000,
                        sort: [{field: "name", direction: "ASC"}]
                    }
                })

                const newServices = response.content || []
                // Storing comma separated resource ids as local storage accepts only string value
                localStorage.removeItem(key)
                localStorage.setItem(
                    key, newServices
                        .map(r => r.id)
                        .join(',')
                )


                dispatch({
                    type: ReservationActions.SET_RESERVATIONS,
                    appId: appId,
                    payload: {
                        selectedServices: newServices
                    }
                })

                queryClient.invalidateQueries('allServices', {exact: false});
                queryClient.invalidateQueries('agenda', {exact: false});
                queryClient.refetchQueries('allServices', {exact: false});

            }
        },
        isValid: (agenda) => {
            return !!agenda.name
        },
        cleanupBeforeSave: (agenda) => {
            return {
                ...agenda,
                name: agenda.name?.trim()
            }
        },
    }, id)

    const agenda = agendaData.item
    const nameMissing = isRequiredTextMissing(agendaData, agenda.name)
    const setAgenda = agendaData.setItem

    //endregion Sate

    //region Handlers

    const updateField = (fields: Partial<AgendaV3>) => {
        setAgenda({...agenda, ...fields});
    };

    //endregion Handlers

    //region UI


    const [searchParams, setSearchParams] = useSearchParams();
    const selectedTab = resolveTab(searchParams.get('tab'), ['basic', 'styling', 'localization'], 'basic');
    const setSelectedTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };
    const organization = useOrganization()
    const previewHref = organization?.name && agenda?.id
        ? `${process.env.REACT_APP_AGENDA_PUBLIC_URL}/${encodeURIComponent(organization.name)}/${agenda.id}`
        : undefined


    const isLoading = agendaData.loading

    const headerSection = (
        <SettingsEntityHeader
            title={agenda.name}
            data={agendaData}
            back={() => navigate('/system/agenda')}
            extraActions={(
                <>
                    {!isMobile && agenda.enableBooking && (
                        <Chip
                            icon={<CheckCircleIcon/>}
                            label={t("Booking Enabled")}
                            color="success"
                            variant="outlined"
                        />
                    )}

                    {previewHref && (
                        isMobile ? (
                            <IconButton
                                component="a"
                                href={previewHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={t('Preview')}
                                size="small"
                                sx={{width: 36, height: 36, borderRadius: 1}}
                            >
                                <OpenInNewIcon fontSize="small"/>
                            </IconButton>
                        ) : (
                            <Button
                                variant="outlined"
                                endIcon={<OpenInNewIcon/>}
                                component="a"
                                href={previewHref}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t("Preview")}
                            </Button>
                        )
                    )}
                </>
            )}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete agenda')}
                description={t('This permanently deletes the agenda and its public link. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={agendaData.saving}
                onDelete={() => agendaData.removeNow()}
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {agenda.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            {isLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px',
                        width: '100%'
                    }}
                >
                    <CircularProgress/>
                </Box>
            ) : (
                <>
                    <Stack
                        sx={{
                            width: '100%',
                            maxWidth: '900px',
                            mx: 'auto',
                            mt: 3,
                            gap: 6,
                        }}
                    >
                        {isMobile && agenda.enableBooking && (
                            <Chip
                                icon={<CheckCircleIcon/>}
                                label={t("Booking Enabled")}
                                color="success"
                                variant="outlined"
                                sx={{alignSelf: 'flex-start'}}
                            />
                        )}

                        <TabContext value={selectedTab}>
                            <ScrollableTabList onChange={(e, v) => setSelectedTab(v)}>
                                <Tab label={t('Basic info')} value={'basic'}/>
                                <Tab label={t('Styling')} value={'styling'}/>
                                <Tab label={t('Localization')} value={'localization'}/>
                            </ScrollableTabList>
                        </TabContext>

                        {selectedTab === 'basic' && (
                            <AgendaDetailsBasicTab
                                agenda={agenda}
                                updateField={updateField}
                                nameMissing={nameMissing}
                            />
                        )}

                        {selectedTab === 'styling' && (
                            <AgendaDetailsStylingTab
                                agenda={agenda}
                                setAgenda={setAgenda}
                            />
                        )}

                        {selectedTab === 'localization' && (
                            <AgendaDetailsLocalizationTab
                                agenda={agenda}
                                updateField={updateField}
                            />
                        )}

                        {
                            selectedTab === 'basic' && !!agenda.id &&
                            <>
                                <Divider sx={{mt: 4}}/>
                                {dangerZoneSection}
                            </>
                        }
                    </Stack>
                </>
            )}

        </Box>
    );
}


export default AgendaDetails;
